import React, { useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { observer } from 'mobx-react-lite';
import { PaymentMethod as PaymentMethodWithFormContext } from '../../../types/Configuration';
import CreditCardWithFormContext, {
  CreditCardFormData,
} from '../../payment/CreditCardWithFormContext';
import AmazonPayCheckoutButton, {
  AmazonPayCheckoutButtonProps,
} from '../../payment/AmazonPayCheckoutButton';
import PayPalCheckoutButton from '../../payment/PayPalCheckoutButton';
import WrappedStripeCheckoutForm from '../../payment/WrappedStripeCheckoutForm';
import PaymentMethod from '../PaymentMethod';
import LabeledTextbox from '../LabeledTextbox';
import AmazonPayLogo from '../../content/AmazonPayLogo';
import { PayPalAcpMethods, PayPalButtonMethods } from '../../../types/PayPal';
import PayPalAdvancedCardProcessing from '../../payment/PayPalAdvancedCardProcessing';
import PayPalMessagesContainer from '../../framework/PayPalMessagesContainer';
import { APIValues } from '../../../utils/constants';
import PayPalLogo from '../../content/PayPalLogo';
import SpreedlyCreditCard from '../../payment/SpreedlyCreditCard';
import { SpreedlyNewPaymentMethodEventHandlers } from '../../../types/Spreedly';
import { useMst } from '../../../models/stores/Root';

export const PaymentMethodFormTestId = 'PaymentMethodForm-input';

export type PaymentMethodFormData = {
  paymentMethodId: number;
  creditCardData?: CreditCardFormData;
  poNumber?: string;
};

export type PaymentMethodFormProps = {
  hasRecurringBillingItem?: boolean;
  paymentMethods: PaymentMethodWithFormContext[];
  payPalButtonMethods: PayPalButtonMethods;
  payPalAcpMethods?: PayPalAcpMethods;
  amazonPayButtonProps: AmazonPayCheckoutButtonProps;
  spreedlyEventHandlers?: SpreedlyNewPaymentMethodEventHandlers;
  onPaymentMethodChanged?: (paymentMethodId: string | null) => void;
  hasGiftCards?: boolean;
  cartTotal?: number;
};

const PaymentMethodForm: React.FC<PaymentMethodFormProps> = observer(
  ({
    hasRecurringBillingItem,
    paymentMethods,
    payPalButtonMethods,
    payPalAcpMethods,
    amazonPayButtonProps,
    onPaymentMethodChanged,
    cartTotal,
    hasGiftCards,
    spreedlyEventHandlers,
  }: PaymentMethodFormProps) => {
    const rootStore = useMst();
    const { configuration } = rootStore;
    const {
      register,
      control,
      formState: { errors },
      setValue,
    } = useFormContext<{
      purchaseOrder: { poNumber: string };
      paymentMethodId: string;
    }>();

    const paymentMethodIdWatch = useWatch({
      control,
      name: 'paymentMethodId',
    });

    const handlePaymentMethodChanged = (selectedPaymentMethodId: string) => {
      onPaymentMethodChanged?.(selectedPaymentMethodId);
    };

    useEffect(() => {
      if (
        (paymentMethodIdWatch === undefined || paymentMethodIdWatch === '') &&
        !configuration.paypalExpress.isDefaultPayment
      ) {
        // If the first method is paypal AND showCreditDebitCardLogos is true, select the credit card option instead of paypal as the default option
        if (paymentMethods[0].methodType === APIValues.PAYPAL_METHOD) {
          if (configuration?.paypalExpress?.showCreditDebitCardLogos) {
            setValue('paymentMethodId', '-1');
          }
        } else {
          setValue('paymentMethodId', paymentMethods[0].id.toString());
        }
      } else if (
        (paymentMethodIdWatch === undefined || paymentMethodIdWatch === '') &&
        configuration.paypalExpress.isDefaultPayment
      ) {
        setValue('paymentMethodId', configuration.defaultPaymentMethodId);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paymentMethodIdWatch]);

    if (hasGiftCards && cartTotal === 0) {
      return (
        <div data-testid={PaymentMethodFormTestId} className="flex flex-col">
          <div className="text-sm ml-2 mt-3 mb-2">
            No additional payment method required
          </div>
        </div>
      );
    }

    return (
      <div
        data-testid={PaymentMethodFormTestId}
        className="flex flex-col mx-auto w-full rounded-md border checkout-panel-border"
      >
        <ul className="flex flex-col divide divide-y">
          {paymentMethods.map(({ id, method, methodType }) => {
            switch (methodType) {
              case 'Credit Card':
                return (
                  <PaymentMethod
                    key={id}
                    inputName="paymentMethodId"
                    label={methodType}
                    paymentMethodId={id}
                    isSelected={paymentMethodIdWatch === id}
                    register={register}
                    defaultChecked={paymentMethodIdWatch === id}
                    onChange={handlePaymentMethodChanged}
                  >
                    {spreedlyEventHandlers ? (
                      <SpreedlyCreditCard
                        spreedlyEventHandlers={spreedlyEventHandlers}
                      />
                    ) : (
                      <CreditCardWithFormContext />
                    )}
                  </PaymentMethod>
                );
              case 'Purchase Order Number':
                return (
                  <PaymentMethod
                    key={id}
                    inputName="paymentMethodId"
                    label={method}
                    paymentMethodId={id}
                    isSelected={paymentMethodIdWatch === id}
                    register={register}
                    defaultChecked={paymentMethodIdWatch === id}
                    onChange={handlePaymentMethodChanged}
                  >
                    <LabeledTextbox
                      name="purchaseOrder.poNumber"
                      label={method}
                      autoComplete="off"
                      errorLabel={errors?.purchaseOrder?.poNumber?.message}
                      register={register}
                    />
                  </PaymentMethod>
                );
              case APIValues.PAYPAL_METHOD:
                return (
                  <div key="paypal-methods">
                    {payPalAcpMethods && !hasRecurringBillingItem && (
                      <PaymentMethod
                        key="acp-hosted-fields"
                        inputName="paymentMethodId"
                        label="Credit Card"
                        paymentMethodId="-1"
                        isSelected={paymentMethodIdWatch === '-1'}
                        register={register}
                        defaultChecked={paymentMethodIdWatch === '-1'}
                        onChange={handlePaymentMethodChanged}
                      >
                        <PayPalAdvancedCardProcessing
                          acpMethods={payPalAcpMethods}
                        />
                      </PaymentMethod>
                    )}
                    <PaymentMethod
                      key={id}
                      inputName="paymentMethodId"
                      label="PayPal"
                      paymentMethodId={id}
                      icon={<PayPalLogo />}
                      message={<PayPalMessagesContainer />}
                      isSelected={paymentMethodIdWatch === id}
                      onChange={handlePaymentMethodChanged}
                      register={register}
                      defaultChecked={paymentMethodIdWatch === id}
                    >
                      <div className="flex flex-col items-center">
                        {!hasRecurringBillingItem ? (
                          <>
                            <div className="text-xs text-center mb-1">
                              Click below to use PayPal
                            </div>
                            <div className="flex flex-col items-center w-72">
                              <PayPalCheckoutButton
                                buttonMethods={payPalButtonMethods}
                              />
                            </div>
                          </>
                        ) : (
                          <div className="text-xs text-center mb-1">
                            <div className="font-bold">
                              Paypal&copy; Express Checkout unavailable
                            </div>
                            <p>
                              Paypal checkout is unavailable for products with
                              recurring billing.
                            </p>
                          </div>
                        )}
                      </div>
                    </PaymentMethod>
                  </div>
                );
              case APIValues.AMAZON_PAY_METHOD:
                return (
                  <PaymentMethod
                    key={id}
                    inputName="paymentMethodId"
                    label={method}
                    paymentMethodId={id}
                    icon={<AmazonPayLogo />}
                    isSelected={paymentMethodIdWatch === id}
                    register={register}
                    onChange={handlePaymentMethodChanged}
                    defaultChecked={paymentMethodIdWatch === id}
                  >
                    <div className="space-y-4 flex flex-col justify-center items-center">
                      {!hasRecurringBillingItem ? (
                        <>
                          <div className="text-xs text-center">
                            Click below to use Amazon Pay
                          </div>
                          <div className="flex flex-col">
                            <AmazonPayCheckoutButton
                              clientId={amazonPayButtonProps.clientId}
                              sellerId={amazonPayButtonProps.sellerId}
                              isSandbox={amazonPayButtonProps.isSandbox}
                              isLoaded={amazonPayButtonProps.isLoaded}
                              onAuthorize={amazonPayButtonProps.onAuthorize}
                              onLoaded={amazonPayButtonProps.onLoaded}
                            />
                          </div>
                        </>
                      ) : (
                        <div className="text-xs text-center mb-1">
                          <div className="font-bold">
                            Amazon Pay is unavailable
                          </div>
                          <p>
                            Amazon Pay is unavailable for products with
                            recurring billing.
                          </p>
                        </div>
                      )}
                    </div>
                  </PaymentMethod>
                );
              case APIValues.STRIPE_METHOD:
                return (
                  <PaymentMethod
                    key={id}
                    inputName="paymentMethodId"
                    label={method}
                    paymentMethodId={id}
                    isSelected={paymentMethodIdWatch === id}
                    register={register}
                    onChange={handlePaymentMethodChanged}
                    defaultChecked={paymentMethodIdWatch === id}
                  >
                    <WrappedStripeCheckoutForm />
                  </PaymentMethod>
                );
              default:
                return (
                  <PaymentMethod
                    key={id}
                    inputName="paymentMethodId"
                    label={method}
                    paymentMethodId={id}
                    isSelected={paymentMethodIdWatch === id}
                    register={register}
                    defaultChecked={paymentMethodIdWatch === id}
                    onChange={handlePaymentMethodChanged}
                  />
                );
            }
          })}
        </ul>
      </div>
    );
  }
);

export default PaymentMethodForm;
